import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useT } from "@/i18n";
import useSectionTheme from "@/hooks/useSectionTheme";
import Logo from "@/components/ui/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useSectionTheme(); // "dark" => white UI, "light" => ink UI

  const isDark = theme === "dark";
  const fg = isDark ? "#ffffff" : "var(--ink)";
  const borderCol = isDark ? "rgba(255,255,255,0.22)" : "rgba(12,14,20,0.22)";
  const hoverBg = isDark ? "#ffffff" : "var(--ink)";
  const hoverFg = isDark ? "var(--ink)" : "#ffffff";

  const LINKS = [
    { id: "hero", label: t("nav.beranda") },
    { id: "values", label: t("nav.about") },
    { id: "mission", label: t("nav.reach") },
    { id: "services", label: t("nav.services") },
    { id: "products", label: t("nav.products") },
    { id: "works", label: t("nav.works") },
    { id: "insights", label: t("nav.insights") },
    { id: "clients", label: t("nav.clients") },
    { id: "contact", label: t("nav.contact") },
  ];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const scroller = document.querySelector(".snap-root");
    if (scroller) {
      // snap-root manages scroll
      scroller.scrollTo({ top: el.offsetTop, behavior: "smooth" });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => scrollToId(id), 120);
      return;
    }
    scrollToId(id);
  };

  return (
    <>
      {/* Floating top bar — no background bar, just floating elements */}
      <div
        className="fixed top-0 left-0 right-0 z-50 pointer-events-none"
        style={{ color: fg }}
      >
        <div className="flex items-start justify-between px-6 md:px-10 pt-6 md:pt-8">
          {/* Left: menu + logo */}
          <div className="flex items-center gap-4 pointer-events-auto">
            <button
              data-testid={NAV.menuToggle}
              onClick={() => setOpen(true)}
              aria-label={t("nav.openMenu")}
              className="w-10 h-10 grid place-items-center rounded-full transition-all duration-300"
              style={{ border: `1px solid ${borderCol}`, color: fg }}
              onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; e.currentTarget.style.color = hoverFg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = fg; }}
            >
              <Menu size={16} />
            </button>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigate("/"); setTimeout(() => scrollToId("hero"), 50); }}
              data-testid={NAV.logo}
              className="flex items-center gap-2"
              style={{ color: fg }}
            >
              <Logo size={28} className="transition-opacity hover:opacity-80" />
              <span className="font-display text-xl font-bold tracking-tight">Makario</span>
            </a>
          </div>

          {/* Right: lang toggle + CTA */}
          <div className="flex items-center gap-3 md:gap-5 pointer-events-auto">
            <div
              data-testid="lang-toggle"
              className="flex items-center text-[0.65rem] font-mono uppercase tracking-[0.22em] rounded-full overflow-hidden"
              style={{ border: `1px solid ${borderCol}`, color: fg }}
            >
              <button
                onClick={() => setLang("en")}
                data-testid="lang-toggle-en"
                aria-pressed={lang === "en"}
                className="px-3 py-1.5 transition-colors"
                style={{
                  background: lang === "en" ? fg : "transparent",
                  color: lang === "en" ? hoverFg : fg,
                }}
              >
                EN
              </button>
              <button
                onClick={() => setLang("id")}
                data-testid="lang-toggle-id"
                aria-pressed={lang === "id"}
                className="px-3 py-1.5 transition-colors"
                style={{
                  background: lang === "id" ? fg : "transparent",
                  color: lang === "id" ? hoverFg : fg,
                }}
              >
                ID
              </button>
            </div>

            <button
              type="button"
              onClick={() => go("contact")}
              data-testid={NAV.ctaContact}
              className="hidden md:inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.65rem] font-mono uppercase tracking-[0.22em] transition-all duration-300"
              style={{
                background: "var(--violet)",
                color: "#fff",
                border: "1px solid var(--violet)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--violet-deep)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "var(--violet)"; }}
            >
              {t("nav.cta")} <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <div
        data-testid={NAV.menuDrawer}
        className={`fixed inset-0 z-[60] transition-all duration-700 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" onClick={() => setOpen(false)} />
        <div
          className={`absolute top-0 left-0 h-full w-full md:w-[520px] bg-[var(--ink)] p-10 md:p-14 flex flex-col transition-transform duration-700 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-3">
              <Logo size={32} />
              <span className="font-display text-2xl font-bold text-white">Makario</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t("nav.closeMenu")}
              className="w-10 h-10 grid place-items-center rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-all"
            >
              <X size={18} />
            </button>
          </div>
          <nav className="flex flex-col gap-5">
            {LINKS.map((l, i) => (
              <button
                key={l.id}
                data-testid={NAV.menuLink(l.id)}
                onClick={() => go(l.id)}
                className="group flex items-baseline gap-4 text-left"
              >
                <span className="font-mono text-xs text-white/40">0{i + 1}</span>
                <span className="font-display text-2xl md:text-3xl font-semibold text-white group-hover:text-[var(--violet-soft)] transition-colors">
                  {l.label}
                </span>
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-12 border-t border-white/10">
            <p className="eyebrow text-white/40 mb-3">{t("nav.contactUs")}</p>
            <a href="mailto:hello@makario.id" className="text-white link-line">hello@makario.id</a>
          </div>
        </div>
      </div>
    </>
  );
}
